<script lang="ts">
    import { navigate, setQuery } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import StageBadge from '../lib/ui/StageBadge.svelte';
  import SkoolLink from '@ui/SkoolLink.svelte';
  import { skoolProfileUrl } from '@ui/member';
  import RoleJourney from '@ui/RoleJourney.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import MembersFilters from '../lib/ui/MembersFilters.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import PlanCell from '@ui/PlanCell.svelte';
  import RenewalCell from '@ui/RenewalCell.svelte';
  import ActivityStrip from '../lib/charts/ActivityStrip.svelte';
  import { agoShort } from '../lib/revenue';
  import { ROLE_ORDER_ALL } from '@ui/quadrant';
  import { STAGE_ORDER } from '@ui/stages';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    memberStatusMeta,
    MEMBER_STATUS_ORDER,
    type AdminUser,
  } from '../lib/admin';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchMembers, journeySegments, NO_MEMBERSHIP, type MemberQuery, type MembersPage, type MemberSort } from '@api';

  const PAGE = 50;

  let me = $state<AdminUser | null>(null);
  let data = $state<MembersPage | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);

  // --- filter state (all facets are multi-select) -----------------------------
  let q = $state('');
  let roles = $state<string[]>([]);
  let hadRoles = $state<string[]>([]);
  let stages = $state<string[]>([]);
  let styles = $state<string[]>([]);
  let statuses = $state<string[]>([]);
  let includeModerators = $state(false);
  // Cohort is a first-contribution deep-link from the retention heatmap, shown as
  // a removable chip rather than a facet.
  let cohort = $state('');
  let retained = $state(0);
  let sort = $state<MemberSort>('activity');
  let order = $state<'asc' | 'desc'>('desc');
  let offset = $state(0);

  // The contribution counts, looped. `Active` is rendered explicitly after them
  // because Plan sits between the two — money last but one, so the row ends on the
  // date that says whether any of it still matters.
  type Col = { key: MemberSort; label: string; num: boolean };
  const COUNT_COLS: Col[] = [
    { key: 'posts', label: 'Posts', num: true },
    { key: 'comments', label: 'Comments', num: true },
  ];

  // URL seeds — consumed once on first load (deep-links from the Overview role,
  // momentum, and retention cards), then cleared on a real community switch.
  // Comma-separated so a single-value legacy link (?quadrant=key_voice) still works.
  const _qs = new URLSearchParams(window.location.search);
  // Includes the terminal `departed` role, so ?quadrant=departed (the census strip's
  // "See who left" link) seeds the facet instead of being dropped as unknown.
  const ROLE_SET = new Set<string>(ROLE_ORDER_ALL);
  // Live stages only — there is no departed momentum chip to seed (stages.ts).
  const STAGE_SET = new Set<string>(STAGE_ORDER);
  const STYLE_SET = new Set(['poster', 'commenter', 'poster_commenter']);
  const STATUS_SET = new Set<string>(MEMBER_STATUS_ORDER);
  function seedList(param: string, valid: Set<string>): string[] {
    return (_qs.get(param) ?? '').split(',').map((s) => s.trim()).filter((s) => valid.has(s));
  }
  const qsCohort = _qs.get('cohort');
  let pendingQ = _qs.get('q') ?? '';
  let pendingRoles = seedList('quadrant', ROLE_SET);
  let pendingHadRoles = seedList('had', ROLE_SET);
  let pendingStages = seedList('stage', STAGE_SET);
  let pendingStyles = seedList('style', STYLE_SET);
  let pendingStatuses = seedList('status', STATUS_SET);
  let pendingMods = _qs.get('mods') === '1' || _qs.get('mods') === 'true';
  let pendingCohort = qsCohort && /^\d{4}-\d{2}$/.test(qsCohort) ? qsCohort : '';
  let pendingRetained = pendingCohort ? Math.max(0, Number(_qs.get('retained')) || 0) : 0;

  // The community whose page is on screen. The effect below reloads on a real
  // switch; `init` has already fetched the first one, so this stops the cold load
  // from firing the same request twice.

  /** Every filter, as the v1 route's query. `include=activity&activity=weekly` is
   *  the whole point of the migration: the legacy row carried a DENSE 371-day
   *  heatmap (20,423 B/row, ≈1,021 KB/page) into a cell that caps its cells at 18px;
   *  53 weekly buckets are ≈1,300 B/row and say the same thing at this size. */
  function query(): MemberQuery {
    return {
      q,
      sort,
      order,
      staff: includeModerators ? 'all' : 'exclude',
      quadrant: roles,
      had_role: hadRoles,
      stage: stages,
      style: styles,
      status: statuses,
      cohort,
      retained,
      limit: PAGE,
      offset,
      withActivity: true,
    };
  }

  /** Adopt the URL seeds as the live filter state. Runs before the first fetch, so
   *  a deep-linked view is one request rather than "load everything, then filter". */
  function applySeeds(): void {
    offset = 0;
    q = pendingQ; pendingQ = '';
    roles = pendingRoles; pendingRoles = [];
    hadRoles = pendingHadRoles; pendingHadRoles = [];
    stages = pendingStages; pendingStages = [];
    styles = pendingStyles; pendingStyles = [];
    statuses = pendingStatuses; pendingStatuses = [];
    includeModerators = pendingMods; pendingMods = false;
    cohort = pendingCohort; pendingCohort = '';
    retained = pendingRetained; pendingRetained = 0;
    syncUrl(); // canonicalise deep-links on first load; clear stale params on a switch
  }

  /**
   * The cold load, in ONE round trip (doc 04 §4.1).
   *
   * The page used to open with `fetchMe()` → `ensureCommunities()` → its own fetch:
   * two round trips before the first byte of real data was even requested, for a
   * community id that was in `localStorage` the whole time. `withCommunity` issues
   * the members request immediately against that id and retries once against the
   * server-resolved one if it turns out to be stale, in parallel with the bootstrap
   * that resolves it.
   *
   * The bootstrap is awaited FIRST although both were issued together: an account
   * still in onboarding must be redirected, not shown a data error, and the members
   * request will legitimately fail for exactly that account.
   */
  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    error = null;
    applySeeds();
    let pageErr: unknown = null;
    const pending = withCommunity((skoolId) => fetchMembers(skoolId, query())).catch(
      (err: unknown) => {
        pageErr = err;
        return null;
      },
    );
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      const page = await pending;
      if (page) {
        data = page;
        watch.seen(community.selectedId);
      } else if (pageErr) {
        throw pageErr;
      }
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // Reset filters and reload when the community changes (untrack so filter/sort
  // edits don't retrigger this effect — they call load() directly). The first
  // community is already on screen from `init`, so it is skipped here: `loading` is
  // true only while `init` is in flight, and without that guard this effect fires
  // the moment the bootstrap lands and re-issues the request already on the wire.
  const watch = onCommunityChange(
    (id) => {
      applySeeds();
      void load(id);
    },
    { gate: () => !loading },
  );

  async function load(skoolId: number | null = community.selectedId): Promise<void> {
    if (skoolId == null) return;
    dataLoading = true;
    error = null;
    try {
      data = await fetchMembers(skoolId, query());
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load members.';
    } finally {
      dataLoading = false;
      loading = false;
    }
  }

  // Mirror the active filters to the URL so a filtered view is shareable.
  function syncUrl(): void {
    setQuery({
      q: q.trim() || null,
      quadrant: roles.length ? roles.join(',') : null,
      had: hadRoles.length ? hadRoles.join(',') : null,
      stage: stages.length ? stages.join(',') : null,
      style: styles.length ? styles.join(',') : null,
      status: statuses.length ? statuses.join(',') : null,
      mods: includeModerators ? '1' : null,
      cohort: cohort || null,
      retained: cohort && retained ? String(retained) : null,
    });
  }

  function applyFilters(): void {
    offset = 0;
    syncUrl();
    void load();
  }

  function clearCohort(): void {
    cohort = '';
    retained = 0;
    offset = 0;
    syncUrl();
    void load();
  }

  function setSort(key: MemberSort): void {
    if (sort === key) {
      order = order === 'desc' ? 'asc' : 'desc';
    } else {
      sort = key;
      // Every metric reads best biggest-first; a renewal date doesn't — the
      // question it answers is "who bills (or lapses) next", i.e. soonest first.
      order = key === 'ends_at' ? 'asc' : 'desc';
    }
    offset = 0;
    void load();
  }
  function prevPage(): void {
    if (offset === 0) return;
    offset = Math.max(0, offset - PAGE);
    void load();
  }
  function nextPage(): void {
    if (offset + PAGE >= total) return;
    offset += PAGE;
    void load();
  }

  // `total` is nullable on the v1 envelope (a route may declare the count too
  // expensive); this one always sends it, and 0 is the honest fallback for the
  // page maths either way.
  const total = $derived(data?.total ?? 0);

  const anyFilter = $derived(
    q.trim() !== '' ||
      roles.length > 0 ||
      hadRoles.length > 0 ||
      stages.length > 0 ||
      styles.length > 0 ||
      statuses.length > 0 ||
      cohort !== '',
  );

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !data}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The title was the count ("120 contributors of 86 current members"), which
           made the one page whose name the sidebar says "Members" the one page that
           never said it. The count is real, so it stays — as the adornment it always
           was, beside the name rather than instead of it. -->
      <PageHeader
        section="Insights"
        title="Members"
        scope="Everyone who has posted or commented, including members who have since left (greyed)"
      >
        {#snippet titleExtra()}
          <span class="font-mono text-sm font-normal text-base-content/70">
            {total.toLocaleString()} contributors
            <!-- The list is the full history but `total_members` counts the room as it
                 stands, so the denominator always says "current" — otherwise the header
                 reads "120 contributors of 86 members" and looks broken. -->
            {#if data?.total_members}<span title="Current members, counted from the roster sweep where we have one">of {data.total_members.toLocaleString()} current</span>{/if}
          </span>
        {/snippet}
      </PageHeader>

      <div class="mb-5">
        <MembersFilters
          bind:q
          bind:roles
          bind:hadRoles
          bind:stages
          bind:styles
          bind:statuses
          bind:includeModerators
          {cohort}
          {retained}
          onClearCohort={clearCohort}
          onchange={applyFilters}
        />
      </div>

      {#if dataLoading && !data}
        <StateBlock state="loading" compact />
      {:else if !data || data.items.length === 0}
        <StateBlock state="empty" message="No members" hint={anyFilter ? 'Nothing matches these filters.' : 'No members ingested yet.'} compact />
      {:else}
        <div class="overflow-x-auto rounded-box border hair bg-base-100">
          <table class="w-full min-w-[60rem] text-sm">
            <thead>
              <tr class="border-b hair bg-base-200/50 text-left">
                <th class="px-3 py-2.5">
                  <button class="eyebrow hover:text-base-content" onclick={() => setSort('handle')}>
                    Member{#if sort === 'handle'}<span class="ml-1 text-primary">{order === 'desc' ? '↓' : '↑'}</span>{/if}
                  </button>
                </th>
                <th class="w-full px-3 py-2.5">
                  <button class="eyebrow hover:text-base-content" onclick={() => setSort('activity')} title="Sorts by total posts + comments · the strip shows the last 12 months, one cell per week">
                    Activity{#if sort === 'activity'}<span class="ml-1 text-primary">{order === 'desc' ? '↓' : '↑'}</span>{/if}
                  </button>
                </th>
                {#each COUNT_COLS as col (col.key)}
                  <th class="whitespace-nowrap px-3 py-2.5 text-right">
                    <button class="eyebrow hover:text-base-content" onclick={() => setSort(col.key)}>
                      {col.label}{#if sort === col.key}<span class="ml-1 text-primary">{order === 'desc' ? '↓' : '↑'}</span>{/if}
                    </button>
                  </th>
                {/each}
                <!-- The billing pair, kept adjacent: what they pay, then when it
                     next moves. -->
                <th class="whitespace-nowrap px-3 py-2.5 text-right">
                  <span class="eyebrow" title="What Skool bills them, and the named tier they sit on — two independent things, so a member can have either, both or neither.">Plan</span>
                </th>
                <th class="whitespace-nowrap px-3 py-2.5 text-right">
                  <button
                    class="eyebrow hover:text-base-content"
                    onclick={() => setSort('ends_at')}
                    title="Next bill — or, for a cancelling member, the day they actually churn. Sort ascending for who lapses next."
                  >
                    Renewal{#if sort === 'ends_at'}<span class="ml-1 text-primary">{order === 'desc' ? '↓' : '↑'}</span>{/if}
                  </button>
                </th>
                <th class="whitespace-nowrap px-3 py-2.5 text-right">
                  <button class="eyebrow hover:text-base-content" onclick={() => setSort('last_active')}>
                    Active{#if sort === 'last_active'}<span class="ml-1 text-primary">{order === 'desc' ? '↓' : '↑'}</span>{/if}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {#each data.items as m, i (m.member_id)}
                {@const plan = m.membership ?? NO_MEMBERSHIP}
                {@const st = m.stats}
                {@const gone = memberStatusMeta(plan.status)?.departed ?? false}
                <tr
                  class={'cursor-pointer border-b hair last:border-0 transition-colors hover:bg-base-200/60 ' +
                    (gone ? 'bg-base-200/40' : '')}
                  onclick={() => navigate(`/admin/community/members/${m.member_id}`)}
                >
                  <td class="px-3 py-2.5 align-middle">
                    <div class="flex items-center gap-2.5">
                      <span class="w-6 shrink-0 text-right font-mono text-xs text-base-content/58">{offset + i + 1}</span>
                      <MemberHover memberId={m.member_id} class="shrink-0">
                        <Avatar src={m.picture_profile} name={m.name} size={36} memberId={m.member_id} />
                      </MemberHover>
                      <div class="w-52 min-w-0">
                        <div class="flex items-center gap-2">
                          <span class={'redact min-w-0 truncate font-medium ' + (gone ? 'text-base-content/60' : '')}>{m.name}</span>
                          {#if st?.stage}
                            <span class="shrink-0"><StageBadge stage={st.stage} momentum={st.momentum} /></span>
                          {/if}
                          <!-- Their profile, without taking the row with it. The whole
                               row navigates to the member page, so this stops the click
                               from doing both — a link inside a click target that also
                               fires the target is a link you cannot use. -->
                          <!-- svelte-ignore a11y_click_events_have_key_events -->
                          <!-- svelte-ignore a11y_no_static_element_interactions -->
                          <span
                            class="shrink-0 text-[0.7rem] text-base-content/55"
                            onclick={(e) => e.stopPropagation()}
                          >
                            <!-- privacy-ok: the handle is only ever a URL here, never text; the title is stripped at runtime -->
                            <SkoolLink href={skoolProfileUrl(m.handle)} title="Open {m.name} on Skool" />
                          </span>
                        </div>
                        {#if m.journey?.length}
                          <div class="mt-1.5">
                            <RoleJourney journey={journeySegments(m.journey)} dense />
                          </div>
                        {/if}
                      </div>
                    </div>
                  </td>
                  <!-- Twelve months of contribution, one cell per week. The dense
                       day grid this replaces was 20 KB a row and was being drawn
                       into the same ~200px of table; a weekly strip is 1.3 KB and
                       reads better at this width because there is no ragged
                       weekday column to align. -->
                  <td class="w-full min-w-[12rem] px-3 py-2.5 align-middle">
                    <ActivityStrip weeks={m.activity?.weeks ?? []} max={m.activity?.max ?? 0} />
                  </td>
                  <td class="whitespace-nowrap px-3 py-2.5 text-right align-middle tabular-nums">{(st?.posts ?? 0).toLocaleString()}</td>
                  <td class="whitespace-nowrap px-3 py-2.5 text-right align-middle tabular-nums">{(st?.comments ?? 0).toLocaleString()}</td>
                  <td class="whitespace-nowrap px-3 py-2.5 align-middle"><PlanCell {plan} /></td>
                  <td class="whitespace-nowrap px-3 py-2.5 align-middle"><RenewalCell row={plan} /></td>
                  <td class="whitespace-nowrap px-3 py-2.5 text-right align-middle font-mono text-xs text-base-content/72">{agoShort(st?.last_active ?? null)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>

        <div class="mt-3 flex items-center justify-between font-mono text-xs text-base-content/72">
          <button class="btn btn-ghost btn-xs" disabled={offset === 0} onclick={prevPage}>← Prev</button>
          <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}</span>
          <button class="btn btn-ghost btn-xs" disabled={offset + PAGE >= total} onclick={nextPage}>Next →</button>
        </div>
      {/if}
    </PageContainer>
  {/if}
</AdminShell>
